import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { getGenderTextColor, getGenderAvatarBg } from '../../utils/genderColors';
import { Button, StatusBadge, DropdownMenu, MobileSearchField, ConfirmationModal } from '../common';
import { COMPETITIVE_HOST_PLAYER_MENU_PROPS } from './competitiveHostPlayerMenuProps';
import PlayerInfoModal from './PlayerInfoModal';
import MatchPreferenceSelector from './MatchPreferenceSelector';
import MensDoublesEligibilityModal from './MensDoublesEligibilityModal';
import { getPlayerAvatarPublicUrl } from '../../services/avatarService';
import { SESSION_PANEL_SURFACE } from '../../utils/sessionMobileLayout';
import {
  getEffectiveGamesThisSession,
  getRemoveFromSessionBlockReason,
  canRemovePlayerFromSession,
} from '../../utils/playerUtils';
import { filterPlayersForHostSearch, getPlayerRosterTypeMeta } from '../../utils/clubPlayerLinkDedup';
import {
  getCompetitiveHostMenuItems,
} from '../../utils/competitivePlayerHostActions';

/**
 * Unified Player Manager Component
 * Supports both competitive and social session variants
 * Uses list layout with buttons for actions
 */
/** Default preview count for club members not yet in the session (search to find others). */
const AVAILABLE_ROSTER_PREVIEW_SIZE = 5;

const DEFAULT_ROSTER_SECTIONS_EXPANDED = {
  playing: true,
  not_started: true,
  paused: true,
  exited: false,
  available: false,
};

const DEFAULT_SECTION_FILTERS = {
  playing: { search: '', sortOrder: 'asc' },
  not_started: { search: '', sortOrder: 'asc' },
  paused: { search: '', sortOrder: 'asc' },
  exited: { search: '', sortOrder: 'asc' },
  available: { search: '', sortOrder: 'asc' },
  signed_up: { search: '', sortOrder: 'asc' },
  cancelled_no_show: { search: '', sortOrder: 'asc' },
};

const COMPETITIVE_ROSTER_GROUP_DEFS = [
  { key: 'playing', id: 'roster-group-playing', title: 'Playing' },
  { key: 'paused', id: 'roster-group-paused', title: 'Paused' },
  { key: 'not_started', id: 'roster-group-not-started', title: 'Signed up' },
  { key: 'exited', id: 'roster-group-exited', title: 'Exited' },
  { key: 'available', id: 'roster-group-available', title: 'Available to add' },
];

const PlayerManager = ({
  variant = 'competitive', // 'competitive' or 'social'
  players = [],
  sessionPlayerStatus = {},
  sessionPlayerExitReasons = {},
  togglePlayerStatus,
  onStartMatchmaking,
  startMatchmakingLoading = false,
  onAddNewPlayer,
  currentClubId,
  onGuestUpdated,
  sessionId = null,
  sessionPlayerPenalties = {},
  onPenaltyUpdated = null,
  seasonId = null,
  /** When set, Pause is disabled for these player IDs (e.g. assigned to an active court). */
  playersOnActiveCourts = null,
  /** Competitive: player IDs in upcoming/next_up slots — remove blocked while set contains player. */
  playersInUpcomingCourts = null,
  /** Competitive: per-player wait metrics including match_preference / preference_strength */
  playerWaitMetrics = {},
  /** Competitive: called after a match preference change so session state refreshes */
  onMatchPreferenceReload = null,
  /** Optional: merge saved preference into local wait metrics before full sync returns. */
  onMatchPreferenceSaved = null,
  /** When false, hide preference controls (host turned off match preferences for the session). */
  matchPreferencesEnabled = true,
  /** playersById map — used for mens ELO context panel in eligibility modal */
  playersById = {},
  /** Competitive: called after host saves mens doubles eligibility so session state refreshes */
  onMensEligibilitySaved = null,
  sessionPlayerScheduledGames = {},
  partnershipLookup = {},
  /** When set, modals are rendered by parent (CompetitiveSessionPage hook). */
  hostActionContext = null,
  openPlayerInfoModal: openPlayerInfoModalProp = null,
  suppressHostModals = false,
  offlineQueue = null,
  session = null,
  getSnapshot = null,
}) => {
  const [activeTab, setActiveTab] = useState(variant === 'social' ? 'playing' : 'all'); // social only
  const [sectionFilters, setSectionFilters] = useState(DEFAULT_SECTION_FILTERS);
  const [showPlayerInfoModal, setShowPlayerInfoModal] = useState(false);
  const [selectedPlayerForInfo, setSelectedPlayerForInfo] = useState(null);
  const [playerInfoModalInitialView, setPlayerInfoModalInitialView] = useState('info');
  const [playerInfoModalEditFocus, setPlayerInfoModalEditFocus] = useState('auto');
  const [rosterSectionsExpanded, setRosterSectionsExpanded] = useState(DEFAULT_ROSTER_SECTIONS_EXPANDED);
  /** Competitive: open Match preference modal from Actions / More */
  const [preferenceModalPlayer, setPreferenceModalPlayer] = useState(null);
  useEffect(() => {
    if (preferenceModalPlayer && preferenceModalPlayer.is_guest !== true) {
      setPreferenceModalPlayer(null);
    }
  }, [preferenceModalPlayer]);
  /** Competitive: open Mens doubles eligibility modal for female players */
  const [eligibilityModalPlayer, setEligibilityModalPlayer] = useState(null);
  const [removeModalPlayer, setRemoveModalPlayer] = useState(null);
  const [removeLoading, setRemoveLoading] = useState(false);

  const useSharedHostActions = variant === 'competitive' && hostActionContext;

  const openPlayerInfoModalInternal = (player, arg = 'info') => {
    let initialViewMode = 'info';
    let initialEditFocus = 'auto';
    if (typeof arg === 'string') {
      initialViewMode = arg;
    } else if (arg && typeof arg === 'object') {
      initialViewMode = arg.initialViewMode ?? 'info';
      initialEditFocus = arg.initialEditFocus ?? 'auto';
    }
    setPlayerInfoModalInitialView(initialViewMode);
    setPlayerInfoModalEditFocus(initialEditFocus);
    setSelectedPlayerForInfo(player);
    setShowPlayerInfoModal(true);
  };

  const openPlayerInfoModal =
    openPlayerInfoModalProp ||
    (useSharedHostActions ? hostActionContext.openPlayerInfoModal : openPlayerInfoModalInternal);

  const toggleRosterSection = (key) => {
    setRosterSectionsExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const updateSectionSearch = (key, search) => {
    setSectionFilters((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || DEFAULT_SECTION_FILTERS[key]), search },
    }));
  };

  const toggleSectionSortOrder = (key) => {
    setSectionFilters((prev) => {
      const current = prev[key] || DEFAULT_SECTION_FILTERS[key];
      return {
        ...prev,
        [key]: { ...current, sortOrder: current.sortOrder === 'asc' ? 'desc' : 'asc' },
      };
    });
  };

  const socialTabs = [
    { key: 'playing', label: 'Playing' },
    { key: 'signed_up', label: 'Signed Up' },
    { key: 'cancelled_no_show', label: 'Cancelled/No Show' },
    { key: 'available', label: 'Available' },
  ];

  const getPlayerDisplayName = (player) =>
    player.name || `${player.first_name || ''} ${player.last_name || ''}`.trim();

  const playerMatchesQuery = (player, query) => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return true;
    return getPlayerDisplayName(player).toLowerCase().includes(normalized);
  };

  const sortPlayersByName = (list, sortOrder) => {
    const next = [...list];
    next.sort((a, b) => {
      const aName = getPlayerDisplayName(a).toLowerCase();
      const bName = getPlayerDisplayName(b).toLowerCase();
      if (sortOrder === 'asc') {
        return aName < bName ? -1 : aName > bName ? 1 : 0;
      }
      return aName > bName ? -1 : aName < bName ? 1 : 0;
    });
    return next;
  };

  const competitiveRosterGroupKey = (player) => {
    const status = sessionPlayerStatus[player.id];
    const exitReason = sessionPlayerExitReasons[player.id];
    if (status === 'active') return 'playing';
    if (status === 'signed_up') return 'not_started';
    if (status === 'paused') return 'paused';
    if (
      (status === 'inactive' && exitReason === 'finished') ||
      status === 'cancelled' ||
      (status === 'inactive' && exitReason === 'no_show')
    ) {
      return 'exited';
    }
    if (status === undefined) return 'available';
    return 'exited';
  };

  const getCompetitiveGroupPlayers = useCallback(
    (groupKey) => {
      const filters = sectionFilters[groupKey] || DEFAULT_SECTION_FILTERS[groupKey];
      const inGroup = players.filter((p) => competitiveRosterGroupKey(p) === groupKey);
      const rosterPool =
        groupKey === 'available'
          ? filterPlayersForHostSearch(inGroup, { sessionPlayerStatus })
          : inGroup;
      const filtered = rosterPool.filter((p) => playerMatchesQuery(p, filters.search));
      return sortPlayersByName(filtered, filters.sortOrder);
    },
    [players, sectionFilters, sessionPlayerStatus, sessionPlayerExitReasons]
  );

  const getSocialTabPlayers = useCallback(
    (tabKey) => {
      const filters = sectionFilters[tabKey] || DEFAULT_SECTION_FILTERS[tabKey];
      const filtered = players.filter((player) => {
        const status = sessionPlayerStatus[player.id];
        const exitReason = sessionPlayerExitReasons[player.id];
        if (!playerMatchesQuery(player, filters.search)) return false;
        if (tabKey === 'playing') return status === 'active';
        if (tabKey === 'signed_up') return status === 'signed_up';
        if (tabKey === 'cancelled_no_show') {
          return status === 'cancelled' || (status === 'inactive' && exitReason === 'no_show');
        }
        if (tabKey === 'available') return status === undefined;
        return false;
      });
      const rosterPool =
        tabKey === 'available'
          ? filterPlayersForHostSearch(filtered, { sessionPlayerStatus })
          : filtered;
      return sortPlayersByName(rosterPool, filters.sortOrder);
    },
    [players, sectionFilters, sessionPlayerStatus, sessionPlayerExitReasons]
  );

  const renderSectionToolbar = (sectionKey, { placeholder = 'Search players…' } = {}) => {
    const filters = sectionFilters[sectionKey] || DEFAULT_SECTION_FILTERS[sectionKey];
    return (
      <div className="mt-2 flex items-center gap-2">
        <MobileSearchField
          placeholder={placeholder}
          value={filters.search}
          onChange={(e) => updateSectionSearch(sectionKey, e.target.value)}
          className="min-w-0 flex-1 form-input rounded-md border border-border bg-surface px-3 py-2 text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label={placeholder}
        />
        <button
          type="button"
          onClick={() => toggleSectionSortOrder(sectionKey)}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border-medium bg-surface text-text-primary hover:bg-surface-subtle focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label={`Sort by name ${filters.sortOrder === 'asc' ? 'A to Z' : 'Z to A'}`}
          title={`Sort by name (${filters.sortOrder === 'asc' ? 'A→Z' : 'Z→A'})`}
        >
          {filters.sortOrder === 'asc' ? '↑' : '↓'}
        </button>
      </div>
    );
  };

  const availableForHostSearch = useMemo(() => {
    const available = players.filter((player) => sessionPlayerStatus[player.id] === undefined);
    return filterPlayersForHostSearch(available, { sessionPlayerStatus });
  }, [players, sessionPlayerStatus]);

  // Count players by status
  const playerCounts = useMemo(() => {
    const counts = variant === 'social' 
      ? { playing: 0, signed_up: 0, cancelled_no_show: 0, available: 0 }
      : { total: 0, active: 0, not_started: 0, paused: 0, finished_cancelled_no_show: 0, not_added: 0 };
    
    players.forEach(player => {
      const status = sessionPlayerStatus[player.id];
      const exitReason = sessionPlayerExitReasons[player.id];
      
      if (variant === 'social') {
        if (status === 'active') {
          counts.playing++;
        } else if (status === 'signed_up') {
          counts.signed_up++;
        } else if (status === 'cancelled' || (status === 'inactive' && exitReason === 'no_show')) {
          counts.cancelled_no_show++;
        } else if (status === undefined) {
          // Count set after loop using deduped available list
        }
      } else {
        // Competitive variant
        if (status === 'active') {
          counts.active++;
        } else if (status === 'signed_up') {
          counts.not_started++;
        } else if (status === 'paused') {
          counts.paused++;
        } else if (
          (status === 'inactive' && exitReason === 'finished') ||
          status === 'cancelled' ||
          (status === 'inactive' && exitReason === 'no_show')
        ) {
          counts.finished_cancelled_no_show++;
        } else if (status === undefined) {
          // Count set after loop using deduped available list
        }
      }
    });
    
    if (variant === 'social') {
      counts.available = availableForHostSearch.length;
    } else if (variant === 'competitive') {
      counts.not_added = availableForHostSearch.length;
      counts.total = counts.active + counts.not_started + counts.paused + counts.finished_cancelled_no_show;
    }
    
    return counts;
  }, [players, sessionPlayerStatus, sessionPlayerExitReasons, variant, availableForHostSearch]);

  const getStatusBadge = (status, exitReason) => {
    if (status === 'active') {
      return <StatusBadge variant="success" size="small">Playing</StatusBadge>;
    } else if (status === 'signed_up') {
      return <StatusBadge variant="primary" size="small">Signed up</StatusBadge>;
    } else if (status === 'paused') {
      return <StatusBadge variant="warning" size="small">Paused</StatusBadge>;
    } else if (status === 'cancelled') {
      return <StatusBadge variant="error" size="small">Cancelled</StatusBadge>;
    } else if (status === 'inactive' && exitReason === 'no_show') {
      return <StatusBadge variant="warning" size="small">No Show</StatusBadge>;
    } else if (status === 'inactive' && exitReason === 'finished') {
      return <StatusBadge variant="info" size="small">Finished</StatusBadge>;
    } else {
      return <StatusBadge variant="ghost" size="small">Not Signed Up</StatusBadge>;
    }
  };

  const tabs = socialTabs.map((tab) => ({
    ...tab,
    count: playerCounts[tab.key] || 0,
  }));

  const closePlayerInfoModal = () => {
    setShowPlayerInfoModal(false);
    setSelectedPlayerForInfo(null);
    setPlayerInfoModalInitialView('info');
    setPlayerInfoModalEditFocus('auto');
  };

  /** Mean effective games for everyone with status "active" — same pool as queue / matchmaking. */
  const sessionPlayingAvgEffectiveGames = useMemo(() => {
    if (variant !== 'competitive' || !sessionId) return null;
    const activePlayers = players.filter((p) => sessionPlayerStatus[p.id] === 'active');
    if (activePlayers.length === 0) return null;
    const sum = activePlayers.reduce((acc, p) => acc + getEffectiveGamesThisSession(p), 0);
    const average = sum / activePlayers.length;
    if (!Number.isFinite(average)) return null;
    return { average, count: activePlayers.length };
  }, [variant, sessionId, players, sessionPlayerStatus]);

  const isOnActiveCourt = (playerId) =>
    playersOnActiveCourts && typeof playersOnActiveCourts.has === 'function' && playersOnActiveCourts.has(playerId);

  const isInUpcomingCourt = (playerId) =>
    playersInUpcomingCourts && typeof playersInUpcomingCourts.has === 'function' && playersInUpcomingCourts.has(playerId);

  const PAUSE_BLOCKED_ON_COURT_MSG =
    'Remove them from the court or finish the match first.';

  const getRemoveBlockDescription = (player) =>
    getRemoveFromSessionBlockReason({
      player,
      isOnCourt: isOnActiveCourt(player.id),
      isInUpcoming: isInUpcomingCourt(player.id),
      sessionPlayerScheduledGames,
      partnershipLookup,
      playersById,
    });

  const canRemoveFromSession = (player) =>
    canRemovePlayerFromSession({
      player,
      isOnCourt: isOnActiveCourt(player.id),
      isInUpcoming: isInUpcomingCourt(player.id),
      sessionPlayerScheduledGames,
      partnershipLookup,
      playersById,
    });

  const buildInternalHostActionContext = () => ({
    sessionId,
    sessionPlayerStatus,
    sessionPlayerExitReasons,
    sessionPlayerScheduledGames,
    partnershipLookup,
    playersById,
    playersOnActiveCourts,
    playersInUpcomingCourts,
    matchPreferencesEnabled,
    togglePlayerStatus,
    openPlayerInfoModal,
    setPreferenceModalPlayer,
    setEligibilityModalPlayer,
    setRemoveModalPlayer,
  });

  const getHostMenuItems = (player, options = {}) => {
    const ctx = useSharedHostActions
      ? { ...hostActionContext, sessionPlayerExitReasons, openPlayerInfoModal }
      : buildInternalHostActionContext();
    return getCompetitiveHostMenuItems(player, ctx, options);
  };

  const handleConfirmRemove = async () => {
    if (!removeModalPlayer) return;
    setRemoveLoading(true);
    try {
      const success = await togglePlayerStatus(removeModalPlayer.id, 'remove');
      if (success !== false) {
        setRemoveModalPlayer(null);
      }
    } finally {
      setRemoveLoading(false);
    }
  };

  const renderCompetitiveActionsTrigger = (label, ariaLabel, extraTriggerClass = '') => (
    <Button
      type="button"
      variant="outline"
      size="small"
      className={`text-xs shrink-0 border-border-medium text-text-secondary min-h-[2rem] sm:min-h-0 ${extraTriggerClass}`}
      aria-label={ariaLabel}
    >
      {label}{' '}
      <span className="ml-0.5 text-[10px] opacity-70" aria-hidden>
        ▾
      </span>
    </Button>
  );

  /**
   * Competitive: details via info icon on name row. Status changes grouped in dropdowns.
   */
  const getCompetitiveRowActions = (player, { compactTrigger = false } = {}) => {
    const status = sessionPlayerStatus[player.id];
    const exitReason = sessionPlayerExitReasons[player.id];
    const triggerCompact = compactTrigger ? '!min-h-0 min-h-0 py-1 px-2' : '';

    if (status === 'active') {
      const items = getHostMenuItems(player);
      if (!items.length) return [];
      return [
        <DropdownMenu
          key={`playing-actions-${player.id}`}
          {...COMPETITIVE_HOST_PLAYER_MENU_PROPS}
          trigger={renderCompetitiveActionsTrigger(
            'Actions',
            'Open actions menu for this player',
            triggerCompact
          )}
          items={items}
        />,
      ];
    }

    if (status === 'signed_up') {
      return [
        <Button
          key={`mark-playing-${player.id}`}
          type="button"
          variant="primary"
          size="small"
          className="text-xs shrink-0 min-h-[2rem] sm:min-h-0"
          onClick={() => togglePlayerStatus(player.id, 'active')}
        >
          <span className="hidden sm:inline">Add to queue</span>
          <span className="sm:hidden">Queue</span>
        </Button>,
        <DropdownMenu
          key={`waiting-actions-${player.id}`}
          {...COMPETITIVE_HOST_PLAYER_MENU_PROPS}
          trigger={renderCompetitiveActionsTrigger(
            'More',
            'Other queue actions for this player',
            triggerCompact
          )}
          items={getHostMenuItems(player)}
        />,
      ];
    }

    if (status === 'paused') {
      return [
        <Button
          key={`resume-${player.id}`}
          type="button"
          variant="primary"
          size="small"
          className="text-xs shrink-0 min-h-[2rem] sm:min-h-0"
          onClick={() => togglePlayerStatus(player.id, 'active')}
        >
          Resume
        </Button>,
        <DropdownMenu
          key={`paused-actions-${player.id}`}
          {...COMPETITIVE_HOST_PLAYER_MENU_PROPS}
          trigger={renderCompetitiveActionsTrigger(
            'More',
            'Other paused player actions',
            triggerCompact
          )}
          items={getHostMenuItems(player)}
        />,
      ];
    }

    if (status === undefined) {
      return [
        <Button
          key={`add-${player.id}`}
          type="button"
          variant="primary"
          size="small"
          className="text-xs shrink-0"
          onClick={() => togglePlayerStatus(player.id, 'signed_up')}
        >
          <span className="hidden sm:inline">Add to session</span>
          <span className="sm:hidden">Add</span>
        </Button>,
      ];
    }

    if (status === 'cancelled' || (status === 'inactive' && exitReason === 'no_show')) {
      return [
        <Button
          key={`readd-${player.id}`}
          type="button"
          variant="primary"
          size="small"
          className="text-xs shrink-0"
          onClick={() => togglePlayerStatus(player.id, 'signed_up')}
        >
          Re-add
        </Button>,
      ];
    }

    if (status === 'inactive' && exitReason === 'finished') {
      return [
        <Button
          key={`readd-${player.id}`}
          type="button"
          variant="primary"
          size="small"
          className="text-xs shrink-0"
          onClick={() => togglePlayerStatus(player.id, 'signed_up')}
        >
          Re-add
        </Button>,
      ];
    }

    return [];
  };

  /** Social / legacy: flat button row (no “More” menu). */
  const getFlatRowActions = (player) => {
    const status = sessionPlayerStatus[player.id];
    const exitReason = sessionPlayerExitReasons[player.id];
    const buttons = [];

    buttons.push(
      <button
        key="view"
        onClick={() => openPlayerInfoModal(player)}
        className="px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm text-text-secondary hover:text-text-primary hover:bg-surface-subtle rounded-md transition-colors border border-border-medium whitespace-nowrap"
      >
        View
      </button>
    );

    const buttonBaseClasses =
      'px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm rounded-md transition-colors border whitespace-nowrap';

    if (status === 'active') {
      buttons.push(
        <button
          key="pause"
          onClick={() => togglePlayerStatus(player.id, 'paused')}
          className={`${buttonBaseClasses} text-warning-dark hover:bg-warning-light border-warning`}
        >
          Pause
        </button>
      );
      buttons.push(
        <button
          key="finished"
          onClick={() => togglePlayerStatus(player.id, 'inactive', 'finished')}
          className={`${buttonBaseClasses} text-text-secondary hover:text-text-primary hover:bg-surface-subtle border-border-medium`}
        >
          Finished
        </button>
      );
      if (canRemoveFromSession(player)) {
        buttons.push(
          <button
            key="remove"
            onClick={() => setRemoveModalPlayer(player)}
            className={`${buttonBaseClasses} text-danger hover:text-danger-dark hover:bg-danger-light border-danger/40`}
          >
            Remove
          </button>
        );
      }
    } else if (status === 'signed_up') {
      buttons.push(
        <button
          key="mark-playing"
          onClick={() => togglePlayerStatus(player.id, 'active')}
          className={`${buttonBaseClasses} text-success-dark hover:bg-success-light border-success`}
        >
          <span className="hidden sm:inline">Mark as Playing</span>
          <span className="sm:hidden">Playing</span>
        </button>
      );
      buttons.push(
        <button
          key="no-show"
          onClick={() => togglePlayerStatus(player.id, 'inactive', 'no_show')}
          className={`${buttonBaseClasses} text-warning-dark hover:bg-warning-light border-warning`}
        >
          No Show
        </button>
      );
      buttons.push(
        <button
          key="cancelled"
          onClick={() => togglePlayerStatus(player.id, 'cancelled', 'cancelled')}
          className={`${buttonBaseClasses} text-danger hover:text-danger-dark hover:bg-danger-light border-danger/40`}
        >
          Cancelled
        </button>
      );
      if (canRemoveFromSession(player)) {
        buttons.push(
          <button
            key="remove"
            onClick={() => setRemoveModalPlayer(player)}
            className={`${buttonBaseClasses} text-danger hover:text-danger-dark hover:bg-danger-light border-danger/40`}
          >
            Remove
          </button>
        );
      }
    } else if (status === 'paused') {
      buttons.push(
        <button
          key="resume"
          onClick={() => togglePlayerStatus(player.id, 'active')}
          className={`${buttonBaseClasses} text-success-dark hover:bg-success-light border-success`}
        >
          Resume
        </button>
      );
      buttons.push(
        <button
          key="signed-up"
          onClick={() => togglePlayerStatus(player.id, 'signed_up')}
          className={`${buttonBaseClasses} text-primary hover:bg-info-light border-primary`}
        >
          <span className="hidden sm:inline">Mark as Signed Up</span>
          <span className="sm:hidden">Sign Up</span>
        </button>
      );
      buttons.push(
        <button
          key="finished"
          onClick={() => togglePlayerStatus(player.id, 'inactive', 'finished')}
          className={`${buttonBaseClasses} text-text-secondary hover:text-text-primary hover:bg-surface-subtle border-border-medium`}
        >
          Finished
        </button>
      );
      if (canRemoveFromSession(player)) {
        buttons.push(
          <button
            key="remove"
            onClick={() => setRemoveModalPlayer(player)}
            className={`${buttonBaseClasses} text-danger hover:text-danger-dark hover:bg-danger-light border-danger/40`}
          >
            Remove
          </button>
        );
      }
    } else if (status === undefined) {
      buttons.push(
        <button
          key="add"
          onClick={() => togglePlayerStatus(player.id, 'signed_up')}
          className={`${buttonBaseClasses} text-primary hover:bg-info-light border-primary`}
        >
          <span className="hidden sm:inline">Add to Session</span>
          <span className="sm:hidden">Add</span>
        </button>
      );
    } else if (status === 'cancelled' || (status === 'inactive' && exitReason === 'no_show')) {
      buttons.push(
        <button
          key="re-add"
          onClick={() => togglePlayerStatus(player.id, 'signed_up')}
          className={`${buttonBaseClasses} text-primary hover:bg-info-light border-primary`}
        >
          Re-add
        </button>
      );
    } else if (status === 'inactive' && exitReason === 'finished') {
      buttons.push(
        <button
          key="re-add"
          onClick={() => togglePlayerStatus(player.id, 'signed_up')}
          className={`${buttonBaseClasses} text-primary hover:bg-info-light border-primary`}
        >
          Re-add
        </button>
      );
    }

    return buttons;
  };

  const getActionButtons = (player, actionOpts = {}) =>
    variant === 'competitive' ? getCompetitiveRowActions(player, actionOpts) : getFlatRowActions(player);

  /** Badge beside status when a female player is flagged mens-doubles-eligible this session. */
  const renderMensEligibleBadge = (playerId) => {
    if (variant !== 'competitive' || !sessionId) return null;
    if (hostActionContext?.hideMensDoublesEligibilityMenu) return null;
    const m = playerWaitMetrics[playerId];
    if (!m?.mensDoublesEligible) return null;
    const elo = m?.mensDoublesShadowElo;
    return (
      <span
        className="inline-flex shrink-0 items-center rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 cursor-help"
        title={`Mens doubles eligible this session${elo != null ? ` — mens ELO: ${elo}` : ''}`}
        aria-label={`Mens doubles eligible${elo != null ? `, mens ELO ${elo}` : ''}`}
      >
        ♂ {elo != null ? elo : ''}
      </span>
    );
  };

  /** Icon beside status badge when a session match preference is set (Playing / Signed Up / Paused). */
  const renderMatchPreferenceIcon = (playerId) => {
    if (!matchPreferencesEnabled) return null;
    if (variant !== 'competitive' || !sessionId) return null;
    const st = sessionPlayerStatus[playerId];
    if (!['active', 'signed_up', 'paused'].includes(st)) return null;
    const m = playerWaitMetrics[playerId];
    const pref = m?.matchPreference || 'no_preference';
    if (pref === 'no_preference') return null;
    const strength = m?.preferenceStrength || 'soft';
    const icon = pref === 'prefer_mixed' ? '🎭' : '⚖️';
    const prefLabel = pref === 'prefer_mixed' ? 'Prefer Mixed' : 'Prefer Levels';
    const isHard = strength === 'hard';
    return (
      <span
        className={`inline-flex shrink-0 cursor-help text-sm leading-none ${isHard ? 'rounded-full px-0.5 ring-1 ring-warning/45' : ''}`}
        title={`${prefLabel} (${isHard ? 'Hard' : 'Soft'}) — change in Actions or More menu`}
        role="img"
        aria-label={`${prefLabel}, ${strength}`}
      >
        {icon}
      </span>
    );
  };

  const renderPlayerRow = (player, { isGridTile = false } = {}) => {
    const status = sessionPlayerStatus[player.id];
    const exitReason = sessionPlayerExitReasons[player.id];
    const playerName = player.name || `${player.first_name || ''} ${player.last_name || ''}`.trim();
    const displayName = playerName || 'Unknown Player';
    const guestTypeMeta = player.is_guest ? getPlayerRosterTypeMeta(player) : null;

    const avatar = (
      <div
        className={`flex flex-shrink-0 items-center justify-center overflow-hidden rounded-full font-semibold text-white ${
          isGridTile ? 'h-9 w-9 text-xs' : 'h-10 w-10 text-sm'
        } ${getGenderAvatarBg(player.gender)}`}
      >
        {player.avatar_url ? (
          <img
            src={getPlayerAvatarPublicUrl(player.avatar_url)}
            alt={playerName}
            className="h-full w-full object-cover"
          />
        ) : (
          (playerName || '?').charAt(0).toUpperCase()
        )}
      </div>
    );

    if (isGridTile) {
      return (
        <div className="flex items-start gap-2 rounded-lg border border-border bg-surface-subtle p-2.5 transition-colors hover:bg-surface">
          {avatar}
          <div className="min-w-0 flex-1">
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
                  <span
                    className={`min-w-0 truncate font-medium ${getGenderTextColor(player.gender)}`}
                    title={displayName}
                  >
                    {displayName}
                  </span>
                  {variant === 'competitive' && (
                    <button
                      type="button"
                      onClick={() => openPlayerInfoModal(player)}
                      className="inline-flex shrink-0 rounded-full p-1 text-text-muted hover:bg-primary/10 hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-surface-subtle"
                      aria-label={`Player details for ${displayName}`}
                      title="Player details"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className="h-3.5 w-3.5 sm:h-4 sm:w-4"
                        aria-hidden
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  )}
                  <div className="flex shrink-0 items-center gap-1 flex-wrap justify-end">
                    <span className="shrink-0">{getStatusBadge(status, exitReason)}</span>
                    {renderMatchPreferenceIcon(player.id)}
                    {renderMensEligibleBadge(player.id)}
                  </div>
                </div>
                {guestTypeMeta && (
                  <span className="mt-0.5 block">
                    <StatusBadge variant={guestTypeMeta.variant} size="small">
                      {guestTypeMeta.label}
                    </StatusBadge>
                  </span>
                )}
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1.5 self-center">
                {getActionButtons(player, { compactTrigger: true })}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-3 rounded-lg border border-border bg-surface-subtle p-3 transition-colors hover:bg-surface sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {avatar}
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
              <span className={`break-words font-medium ${getGenderTextColor(player.gender)}`} title={displayName}>
                {displayName}
              </span>
              {variant === 'competitive' && (
                <button
                  type="button"
                  onClick={() => openPlayerInfoModal(player)}
                  className="inline-flex shrink-0 rounded-full p-1.5 text-text-muted hover:bg-primary/10 hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-surface-subtle"
                  aria-label={`Player details for ${displayName}`}
                  title="Player details"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="h-4 w-4"
                    aria-hidden
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              )}
              <div className="flex shrink-0 items-center gap-1 flex-wrap justify-end">
                {getStatusBadge(status, exitReason)}
                {renderMatchPreferenceIcon(player.id)}
                {renderMensEligibleBadge(player.id)}
              </div>
            </div>
            {guestTypeMeta && (
              <span className="mt-0.5 block">
                <StatusBadge variant={guestTypeMeta.variant} size="small">
                  {guestTypeMeta.label}
                </StatusBadge>
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-start gap-1.5 sm:justify-end sm:gap-2">
          {getActionButtons(player)}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Action Buttons - Responsive layout */}
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
        {variant === 'competitive' && onStartMatchmaking && playerCounts.not_started > 0 ? (
          <Button
            onClick={onStartMatchmaking}
            variant="primary"
            size="small"
            className="w-full px-3 py-1 text-xs sm:w-auto"
            disabled={startMatchmakingLoading}
          >
            {startMatchmakingLoading
              ? 'Adding to queue…'
              : 'Add all sign ups to queue'}
          </Button>
        ) : null}
        <Button
          onClick={onAddNewPlayer}
          variant="secondary"
          size="small"
          className="text-xs px-3 py-1 w-full sm:w-auto"
        >
          + Add Player
        </Button>
      </div>

      {variant === 'social' && (
        <div className="border-b border-border-medium">
          <nav className="-mb-px flex flex-wrap items-center gap-x-4 gap-y-2">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium ${
                  activeTab === tab.key
                    ? 'border-primary text-primary'
                    : 'border-transparent text-text-muted hover:border-border hover:text-text-secondary'
                }`}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span
                    className={`ml-2 rounded-full px-2 py-0.5 text-xs ${
                      activeTab === tab.key
                        ? 'bg-info-light text-info-dark'
                        : 'bg-surface-subtle text-text-secondary'
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
      )}

      <div className={SESSION_PANEL_SURFACE}>
        {variant === 'competitive' ? (
          <div className="space-y-6">
            {COMPETITIVE_ROSTER_GROUP_DEFS.map((group) => {
              const isAvailableGroup = group.key === 'available';
              const allInGroup = getCompetitiveGroupPlayers(group.key);
              const totalInGroup = allInGroup.length;
              const unfilteredGroupCount =
                group.key === 'available'
                  ? availableForHostSearch.length
                  : players.filter((p) => competitiveRosterGroupKey(p) === group.key).length;
              const sectionSearch = (sectionFilters[group.key] || DEFAULT_SECTION_FILTERS[group.key]).search;
              const availableSearchActive = sectionSearch.trim().length > 0;
              const displayedPlayers = isAvailableGroup
                ? availableSearchActive
                  ? allInGroup
                  : allInGroup.slice(0, AVAILABLE_ROSTER_PREVIEW_SIZE)
                : allInGroup;
              const headerCount = unfilteredGroupCount;
              const isExpanded = rosterSectionsExpanded[group.key] ?? true;
              const panelId = `${group.id}-panel`;

              return (
                <section key={group.key} id={group.id} className="scroll-mt-28">
                  <button
                    type="button"
                    onClick={() => toggleRosterSection(group.key)}
                    className="mb-1 flex w-full items-center justify-between gap-3 border-b border-border-medium pb-2 text-left transition hover:bg-surface-subtle/50 rounded-t-lg -mx-1 px-1"
                    aria-expanded={isExpanded}
                    aria-controls={panelId}
                  >
                    <h3 className="text-base font-semibold text-text-primary">{group.title}</h3>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="text-sm font-medium tabular-nums text-text-secondary">
                        {headerCount} {headerCount === 1 ? 'player' : 'players'}
                      </span>
                      <svg
                        className={`h-5 w-5 shrink-0 text-text-secondary transition-transform duration-200 ${
                          isExpanded ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </button>
                  {isExpanded ? (
                    <div id={panelId}>
                      {renderSectionToolbar(group.key, {
                        placeholder: isAvailableGroup
                          ? 'Search club members to add…'
                          : 'Search players…',
                      })}
                      {totalInGroup === 0 ? (
                        <p className="py-3 text-sm text-text-muted">
                          {isAvailableGroup && availableSearchActive
                            ? 'No club members match your search.'
                            : 'No players in this group.'}
                        </p>
                      ) : (
                        <>
                          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                            {displayedPlayers.map((player) => (
                              <React.Fragment key={player.id}>
                                {renderPlayerRow(player, { isGridTile: true })}
                              </React.Fragment>
                            ))}
                          </div>
                          {isAvailableGroup &&
                          !availableSearchActive &&
                          headerCount > AVAILABLE_ROSTER_PREVIEW_SIZE ? (
                            <p className="mt-3 text-xs text-text-muted">
                              Showing {AVAILABLE_ROSTER_PREVIEW_SIZE} of {headerCount}. Search above to find
                              someone else.
                            </p>
                          ) : null}
                        </>
                      )}
                    </div>
                  ) : null}
                </section>
              );
            })}
          </div>
        ) : (() => {
          const socialPlayers = getSocialTabPlayers(activeTab);
          const socialSearchActive = (sectionFilters[activeTab]?.search || '').trim().length > 0;
          if (socialPlayers.length === 0) {
            return (
              <div className="py-8 text-center text-text-muted">
                <p>{socialSearchActive ? 'No players match your search.' : 'No players found in this category.'}</p>
                {!socialSearchActive && activeTab === 'available' ? (
                  <p className="mt-2 text-sm">All club players are already signed up.</p>
                ) : null}
              </div>
            );
          }
          return (
            <>
              {renderSectionToolbar(activeTab, {
                placeholder:
                  activeTab === 'available' ? 'Search club members to add…' : 'Search players…',
              })}
              <div className="mt-2 space-y-2">
                {socialPlayers.map((player) => (
                  <React.Fragment key={player.id}>{renderPlayerRow(player)}</React.Fragment>
                ))}
              </div>
            </>
          );
        })()}
      </div>

      {/* Player Info Modal — skipped when parent renders host action modals */}
      {!suppressHostModals && showPlayerInfoModal && selectedPlayerForInfo && (
        <PlayerInfoModal
          isOpen={showPlayerInfoModal}
          onClose={closePlayerInfoModal}
          player={selectedPlayerForInfo}
          clubId={currentClubId}
          seasonId={seasonId}
          sessionId={sessionId}
          onGuestUpdated={onGuestUpdated}
          onPenaltyUpdated={onPenaltyUpdated}
          sessionPlayerStatus={sessionPlayerStatus}
          sessionPlayerPenalties={sessionPlayerPenalties}
          initialViewMode={playerInfoModalInitialView}
          initialEditFocus={playerInfoModalEditFocus}
          sessionPlayingAvgEffectiveGames={sessionPlayingAvgEffectiveGames}
        />
      )}

      {!suppressHostModals && variant === 'competitive' && preferenceModalPlayer?.is_guest && sessionId && (
        <MatchPreferenceSelector
          variant="modal"
          open
          onClose={() => setPreferenceModalPlayer(null)}
          playerId={preferenceModalPlayer.id}
          sessionId={sessionId}
          currentPreference={
            playerWaitMetrics[preferenceModalPlayer.id]?.matchPreference || 'no_preference'
          }
          currentStrength={playerWaitMetrics[preferenceModalPlayer.id]?.preferenceStrength || 'soft'}
          offlineQueue={offlineQueue}
          session={session}
          getSnapshot={getSnapshot}
          playerName={
            preferenceModalPlayer.name ||
            `${preferenceModalPlayer.first_name || ''} ${preferenceModalPlayer.last_name || ''}`.trim() ||
            'Player'
          }
          onPreferenceChange={(playerId, newPref, newStrength, rowUpdatedAt) => {
            if (onMatchPreferenceSaved) onMatchPreferenceSaved(playerId, newPref, newStrength, rowUpdatedAt);
            if (onMatchPreferenceReload) onMatchPreferenceReload();
            setPreferenceModalPlayer(null);
          }}
        />
      )}

      {!suppressHostModals && variant === 'competitive' && eligibilityModalPlayer && sessionId && (
        <MensDoublesEligibilityModal
          open
          onClose={() => setEligibilityModalPlayer(null)}
          player={eligibilityModalPlayer}
          sessionId={sessionId}
          currentEligible={
            playerWaitMetrics[eligibilityModalPlayer.id]?.mensDoublesEligible || false
          }
          currentShadowElo={
            playerWaitMetrics[eligibilityModalPlayer.id]?.mensDoublesShadowElo ?? null
          }
          playersById={playersById}
          onSaved={(playerId, eligible, shadowElo) => {
            if (onMensEligibilitySaved) onMensEligibilitySaved(playerId, eligible, shadowElo);
          }}
        />
      )}

      {!suppressHostModals && (
      <ConfirmationModal
        isOpen={!!removeModalPlayer}
        onClose={() => setRemoveModalPlayer(null)}
        onConfirm={handleConfirmRemove}
        title="Remove from session"
        message={
          removeModalPlayer
            ? `Remove ${getPlayerDisplayName(removeModalPlayer)} from this session? They will return to the available list.`
            : ''
        }
        confirmText="Remove"
        isDangerous
        loading={removeLoading}
      />
      )}
    </div>
  );
};

export default PlayerManager;

